import { referenceDisplay } from '@seen/core/display'
import type {
  ExtendedResource,
  ExtendedResourceConstructor,
} from '@seen/core/ExtendedResource'
import { CompositionNote } from '@seen/core/models/compositionNote'
import { getType } from '@seen/core/utils'
import { Button, cn, DotSeparator } from '@seen/ui'
import { IconCheck } from '@tabler/icons-react'
import { type ReactNode, useCallback, useMemo, useState } from 'react'
import { Card } from '#src/components/core/Card.tsx'
import {
  Command,
  CommandItem,
  CommandList,
} from '#src/components/core/Command.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'
import { PractitionerNameplate } from '#src/components/nameplates/PractitionerNameplate.tsx'
import { UserPlaceholderNameplate } from '#src/components/nameplates/UserPlaceholderNameplate.tsx'
import { RichText } from '#src/components/RichText.tsx'
import { CompositionContext } from '#src/contexts/CompositionContext.tsx'
import { useDidChange } from '#src/hooks/useDidChange.ts'
import { useResourceVersions } from '#src/hooks/useResourceVersions.ts'
import { parseNoteContent } from '#src/utils/noteContent.ts'

export interface VersionHistoryDialogProps<TResource extends ExtendedResource> {
  open: boolean
  onOpenChange: (open: boolean) => void
  resource: TResource | undefined
  renderVersion?: (version: TResource) => ReactNode
  canRestore?: boolean
  onRestoreVersion: (version: TResource) => void
  /** Shown when the resource has no recorded history yet. */
  emptyState?: ReactNode
}

export function VersionHistoryDialog<TResource extends ExtendedResource>({
  open,
  onOpenChange,
  resource,
  renderVersion,
  canRestore = true,
  onRestoreVersion,
  emptyState,
}: VersionHistoryDialogProps<TResource>): ReactNode {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogDisplay notScrollable size="lg">
        {resource ? (
          <VersionHistoryDialogContent
            resource={resource}
            renderVersion={renderVersion}
            canRestore={canRestore}
            emptyState={emptyState}
            onRestoreVersion={(version) => {
              onRestoreVersion(version)
              onOpenChange(false)
            }}
          />
        ) : (
          <VersionHistoryLoading />
        )}
      </DialogDisplay>
    </Dialog>
  )
}

function VersionHistoryDialogContent<TResource extends ExtendedResource>({
  resource,
  renderVersion,
  canRestore,
  onRestoreVersion,
  emptyState,
}: {
  resource: TResource
  renderVersion?: (version: TResource) => ReactNode
  canRestore: boolean
  onRestoreVersion: (version: TResource) => void
  emptyState?: ReactNode
}): ReactNode {
  const resourceClass =
    resource.constructor as ExtendedResourceConstructor<TResource>
  const {
    data: versions = [],
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useResourceVersions(resourceClass, resource.id)

  const latestVersion = versions[0]?.versionId
  const [selectedVersion, setSelectedVersion] = useState<string | null>(
    latestVersion ?? null
  )
  const didLatestVersionChange = useDidChange(latestVersion)
  if (didLatestVersionChange) {
    setSelectedVersion(latestVersion ?? null)
  }

  const selectedHistoryVersion = useMemo(() => {
    return versions.find((v) => v.versionId === selectedVersion)
  }, [versions, selectedVersion])

  const handleRestoreVersion = useCallback(() => {
    if (selectedHistoryVersion) {
      onRestoreVersion(selectedHistoryVersion)
    }
  }, [selectedHistoryVersion, onRestoreVersion])

  if (isLoading) {
    return <VersionHistoryLoading />
  }

  if (versions.length === 0) {
    return (
      <div className="flex flex-col">
        <DialogTitle className="p-6">Version History</DialogTitle>
        <DialogContent className="min-h-0 flex-1">
          <div className="flex flex-1 items-center justify-center p-8 text-foreground-secondary">
            {emptyState ?? 'No version history yet.'}
          </div>
        </DialogContent>
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <DialogTitle className="flex items-center gap-4 p-6">
        <span>Version History</span>
        {canRestore &&
          selectedHistoryVersion &&
          selectedVersion !== latestVersion && (
            <div className="-my-4">
              <Button onClick={handleRestoreVersion}>
                Restore this version
              </Button>
            </div>
          )}
      </DialogTitle>
      <DialogContent className="min-h-0 flex-1">
        <div className="flex min-h-0 flex-1 gap-4">
          <Card className="flex max-w-[calc(65ch+2rem)] min-w-[calc(65ch+2rem)] flex-col p-0 text-sm">
            <ScrollArea
              scrollRestorationId={`${resource.reference.reference}-version-${selectedVersion}`}
            >
              {selectedHistoryVersion &&
                (renderVersion ? (
                  renderVersion(selectedHistoryVersion)
                ) : (
                  <DefaultVersionPreview version={selectedHistoryVersion} />
                ))}
            </ScrollArea>
          </Card>
          <Card className="flex-1 p-0">
            <Command>
              <CommandList className="max-h-none">
                {versions.map((version) => {
                  const practitionerAuthor = version.lastUpdatedByPractitioner
                  const author = version.resourceAuthor
                  return (
                    <CommandItem
                      key={version.versionId}
                      value={version.versionId}
                      onSelect={() => setSelectedVersion(version.versionId)}
                    >
                      <div className="flex flex-1 items-center justify-between">
                        <div className="ml-0.5 flex items-center gap-1">
                          {practitionerAuthor ? (
                            <PractitionerNameplate
                              practitioner={practitionerAuthor}
                            />
                          ) : (
                            <UserPlaceholderNameplate>
                              {referenceDisplay(author) ??
                                getType(author) ??
                                'System'}
                            </UserPlaceholderNameplate>
                          )}
                          <DotSeparator />
                          <HoverDate
                            date={version.lastUpdated}
                            format="datetime"
                          />
                        </div>

                        <IconCheck
                          className={cn(
                            'mr-0.5 size-4 shrink-0 opacity-0',
                            selectedVersion === version.versionId &&
                              'opacity-100'
                          )}
                        />
                      </div>
                    </CommandItem>
                  )
                })}
                {hasNextPage && (
                  <CommandItem
                    value="load-older-versions"
                    disabled={isFetchingNextPage}
                    className="justify-center text-foreground-secondary"
                    onSelect={() => {
                      void fetchNextPage()
                    }}
                  >
                    {isFetchingNextPage
                      ? 'Loading older versions…'
                      : 'Load older versions'}
                  </CommandItem>
                )}
              </CommandList>
            </Command>
          </Card>
        </div>
      </DialogContent>
    </div>
  )
}

function VersionHistoryLoading(): ReactNode {
  return (
    <div className="flex flex-col">
      <DialogTitle className="p-6">Version History</DialogTitle>
      <DialogContent className="min-h-0 flex-1">
        <div className="flex min-h-0 flex-1 gap-4">
          <Card className="flex max-w-[calc(65ch+2rem)] min-w-[calc(65ch+2rem)] flex-col p-0 text-sm">
            <div className="h-screen">
              <Loader />
            </div>
          </Card>
          <Card className="flex-1 p-0">
            <Loader />
          </Card>
        </div>
      </DialogContent>
    </div>
  )
}

function DefaultVersionPreview<TResource extends ExtendedResource>({
  version,
}: {
  version: TResource
}): ReactNode {
  if (!(version instanceof CompositionNote)) {
    return null
  }

  return (
    <CompositionContext.Provider value={version.reference.reference}>
      <RichText className="p-4" value={parseNoteContent(version.content)} />
    </CompositionContext.Provider>
  )
}
